// ── Claude Code plugin-directory generator (STATELESS) ──────────────────────
// Generates the contents of a Claude Code plugin directory
// (`.claude/plugins/skaile/`) as an in-memory file map. No plugin instance or
// boot() is required — safe to call from install commands.
//
// The previous standalone `@skaile/workspace-plugin-claude` package shipped
// these as static files plus loose `.mjs` hook scripts; they are now
// CLI-owned templates. Hooks invoke the `skaile` binary directly
// (`skaile plugin hook <event>`) and MCP runs via `skaile mcp-server` — no
// generated script files, no shebang, no runtime assumption.

/**
 * The Claude Code plugin manifest. `name` is `skaile`; `hooks` and
 * `mcpServers` point at sibling files in the generated directory.
 */
const PLUGIN_JSON = {
  name: "skaile",
  version: "0.1.0",
  description: "Skaile workspace — connectors, mounts, skills, flows, shared state",
  author: { name: "skaile-ai", url: "https://github.com/skaile-ai" },
  repository: "https://github.com/skaile-ai/workspaces",
  keywords: ["skaile", "connectors", "workspace", "ai-assets"],
  hooks: "./hooks/hooks.json",
  mcpServers: "./.mcp.json",
  userConfig: {
    skaile_yaml_path: {
      type: "string",
      title: "skaile.yaml path",
      description: "Override path to skaile.yaml (default: auto-detect from project root)",
    },
  },
};

/**
 * Hook wiring. Each lifecycle event invokes the `skaile` binary directly —
 * no loose `.mjs` scripts. `skaile plugin hook <event>` resolves the project
 * directory itself (`CLAUDE_PROJECT_DIR` / cwd) and always exits 0.
 */
const HOOKS_JSON = {
  hooks: {
    SessionStart: [{ hooks: [{ type: "command", command: "skaile plugin hook session-start" }] }],
    SessionEnd: [{ hooks: [{ type: "command", command: "skaile plugin hook session-end" }] }],
    UserPromptSubmit: [
      { hooks: [{ type: "command", command: "skaile plugin hook user-prompt-submit" }] },
    ],
  },
};

/**
 * MCP server wiring. Spawns `skaile mcp-server` — a stdio MCP subprocess that
 * runs on plain Node (no Claude Agent SDK).
 */
const MCP_JSON = {
  mcpServers: {
    "skaile-workspace": {
      command: "skaile",
      args: ["mcp-server"],
      env: {},
    },
  },
};

/**
 * Build the Claude Code plugin directory as an in-memory file map.
 *
 * Returns `{ relativePath: contents }` for every file the installer must
 * write under `<projectDir>/.claude/plugins/skaile/`. The installer is also
 * responsible for populating `skills/` from the project's `.skaile/skills/`.
 *
 * Stateless — no plugin instance, no `boot()`. The generated artifacts carry
 * no project-specific paths: hooks and the MCP server resolve the project
 * directory themselves at runtime.
 *
 * @returns A map of plugin-relative file paths to their string contents.
 *
 * @docLink packages/workspace-plugin/concepts#adapters
 */
export function buildClaudePluginFiles(): Record<string, string> {
  return {
    ".claude-plugin/plugin.json": `${JSON.stringify(PLUGIN_JSON, null, 2)}\n`,
    "hooks/hooks.json": `${JSON.stringify(HOOKS_JSON, null, 2)}\n`,
    ".mcp.json": `${JSON.stringify(MCP_JSON, null, 2)}\n`,
    "skills/.gitkeep": "",
  };
}
